/*
 * Image Cloud Gallery Styles
 * All classes prefixed with fbn-ic- to avoid conflicts with host applications
 */

/* CSS Custom Properties for Theming - scoped to gallery container */
.fbn-ic-gallery {
    --fbn-ic-bg-primary: #05060F;
    --fbn-ic-bg-secondary: #1a1a2e;
    --fbn-ic-bg-glass: rgba(255, 255, 255, 0.05);
    --fbn-ic-border-glass: rgba(255, 255, 255, 0.1);

    --fbn-ic-text-primary: #ffffff;
    --fbn-ic-text-secondary: #b8b8d1;
    --fbn-ic-text-muted: #6b6b8f;

    --fbn-ic-accent-primary: #6366f1;
    --fbn-ic-accent-secondary: #8b5cf6;
    --fbn-ic-accent-glow: rgba(99, 102, 241, 0.4);

    --fbn-ic-transition-smooth: cubic-bezier(0.4, 0.0, 0.2, 1);
    --fbn-ic-transition-bounce: cubic-bezier(0.68, -0.55, 0.265, 1.55);

    --fbn-ic-shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.3);
    --fbn-ic-shadow-md: 0 4px 16px rgba(0, 0, 0, 0.4);
    --fbn-ic-shadow-lg: 0 8px 32px rgba(0, 0, 0, 0.5);
    --fbn-ic-shadow-glow: 0 0 20px var(--fbn-ic-accent-glow);
}

/* Gallery Container */
.fbn-ic-gallery {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    perspective: 1000px;
}

/* Cloud Images */
.fbn-ic-image {
    position: absolute;
    /* width and height are set dynamically via JavaScript */
    /* border-radius, box-shadow, filter, opacity, cursor are now configurable via styling config */
    /* Inline styles from JS will override these fallbacks when styling config is provided */
    border-radius: 8px;
    box-shadow: var(--fbn-ic-shadow-md);
    cursor: pointer;
    transition: transform 0.6s var(--fbn-ic-transition-smooth),
        box-shadow 0.6s var(--fbn-ic-transition-smooth),
        filter 0.3s var(--fbn-ic-transition-smooth),
        opacity 0.3s var(--fbn-ic-transition-smooth),
        border 0.3s var(--fbn-ic-transition-smooth),
        outline 0.3s var(--fbn-ic-transition-smooth),
        z-index 0s 0.6s;
    will-change: transform;
    user-select: none;
    /* GPU compositing for smooth animations and better zoom quality */
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
}

/* Hover styles are applied via JS when styling config is provided */
/* These CSS rules serve as fallbacks for default behavior */
.fbn-ic-image:hover {
    box-shadow: var(--fbn-ic-shadow-lg);
}

.fbn-ic-image.fbn-ic-focused {
    z-index: 1000;
    /* Focused styles are applied via JS when styling config is provided */
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.7), var(--fbn-ic-shadow-glow);
    transition: transform 0.6s var(--fbn-ic-transition-smooth),
        box-shadow 0.6s var(--fbn-ic-transition-smooth),
        filter 0.3s var(--fbn-ic-transition-smooth),
        opacity 0.3s var(--fbn-ic-transition-smooth),
        border 0.3s var(--fbn-ic-transition-smooth),
        outline 0.3s var(--fbn-ic-transition-smooth),
        z-index 0s 0s;
    /* Remove will-change to allow browser to re-rasterize at zoom scale */
    will-change: auto;
}

/* Loading State */
.fbn-ic-loading {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
    color: var(--fbn-ic-text-secondary);
    z-index: 100;
    pointer-events: none;
}

.fbn-ic-spinner {
    width: 50px;
    height: 50px;
    margin: 0 auto 1rem;
    border: 4px solid var(--fbn-ic-border-glass);
    border-top: 4px solid var(--fbn-ic-accent-primary);
    border-radius: 50%;
    animation: fbn-ic-spin 1s linear infinite;
}

@keyframes fbn-ic-spin {
    to {
        transform: rotate(360deg);
    }
}

/* Error State */
.fbn-ic-error {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    max-width: 600px;
    padding: 1.5rem;
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    border-radius: 12px;
    color: #fca5a5;
    text-align: center;
    backdrop-filter: blur(10px);
    z-index: 100;
}

/* Image Counter */
.fbn-ic-counter {
    background: rgba(0, 0, 0, 0.6);
    color: #fff;
    padding: 6px 16px;
    border-radius: 16px;
    font-family: system-ui, sans-serif;
    font-size: 14px;
}

/* Focus Outline (keyboard navigation indicator) */
.fbn-ic-gallery.fbn-ic-suppress-outline.fbn-ic-has-focus {
    outline: 2px solid var(--fbn-ic-accent-primary);
    outline-offset: -4px;
}

/* Navigation Buttons */
.fbn-ic-nav-btn {
    background: rgba(0, 0, 0, 0.6);
    color: #fff;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    font-family: system-ui, sans-serif;
    font-size: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s ease, opacity 0.2s ease;
    opacity: 0.8;
}
.fbn-ic-nav-btn:hover {
    background: rgba(0, 0, 0, 0.85);
    opacity: 1.0;
}

/* Utility Classes */
.fbn-ic-hidden {
    display: none !important;
}

/* Screen-reader only — visually hidden but announced by assistive tech */
.fbn-ic-sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

/* Responsive Design */
/* Image sizes are now controlled via JavaScript adaptive sizing, not CSS media queries */
